import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';
import api from '../../../services/api';
import EmployeeForm from './EmployeeForm';
import EmployeeProfileModal from './EmployeeProfileModal';
import Departments from '../Settings/Departments';
import CustomRoles from '../Settings/CustomRoles';
import HybridScheduleModal from '../../../components/HybridScheduleModal';

const Employees = () => {
  const [employees, setEmployees] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isDeptModalOpen, setIsDeptModalOpen] = useState(false);
  const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
  const [isHybridModalOpen, setIsHybridModalOpen] = useState(false);
  const [selectedHybridEmployee, setSelectedHybridEmployee] = useState(null);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [initialTab, setInitialTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All Roles');
  const [departmentFilter, setDepartmentFilter] = useState('All Departments');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [locationFilter, setLocationFilter] = useState('All Locations');
  const [customRoles, setCustomRoles] = useState([]);
  const uniqueDepartments = ['All Departments', ...new Set(employees.map(e => e.department?.name).filter(Boolean))];

  const fetchCustomRoles = async () => {
    try {
      const res = await api.get('/custom-roles');
      setCustomRoles(res.data);
    } catch (e) {
      console.error('Failed to fetch custom roles');
    }
  };

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const res = await api.get('/employees');
      setEmployees(res.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
    fetchCustomRoles();
  }, []);

  const handleCreate = () => {
    setEditingEmployee(null);
    setInitialTab('details');
    setIsFormOpen(true);
  };

  const handleEdit = (employee, e) => {
    if (e) e.stopPropagation();
    setEditingEmployee(employee);
    setInitialTab('details');
    setIsProfileOpen(false);
    setIsFormOpen(true);
  };

  const handleViewProfile = (employee) => {
    setEditingEmployee(employee);
    setIsProfileOpen(true);
  };

  const handleToggleActive = async (emp, e) => {
    e.stopPropagation();
    const willActivate = !emp.isActive;
    const confirmMsg = willActivate
      ? `Are you sure you want to activate/restore ${emp.firstName} ${emp.lastName}?`
      : `Are you sure you want to deactivate ${emp.firstName} ${emp.lastName}?`;

    if (window.confirm(confirmMsg)) {
      try {
        await api.put(`/employees/${emp._id}`, { 
          isActive: willActivate 
        });
        fetchEmployees();
      } catch (error) {
        console.error(`Failed to ${willActivate ? 'activate' : 'deactivate'} employee:`, error);
        alert(error.response?.data?.message || `Failed to update employee status`);
      }
    }
  };

  const handleFormClose = (shouldRefresh) => {
    setIsFormOpen(false);
    if (shouldRefresh) {
      fetchEmployees();
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center bg-white p-6 rounded-2xl shadow-sm border border-gray-100 gap-4">
        <div>
          <h2 className="text-xl font-bold text-gray-800">Team Directory</h2>
          <p className="text-sm text-gray-500 mt-1">Manage all your employees and their access roles.</p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <button
            onClick={() => setIsRolesModalOpen(true)}
            className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl shadow-sm transition-all duration-200 font-medium"
          >
            Manage Roles
          </button>
          <button
            onClick={() => setIsDeptModalOpen(true)}
            className="bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl shadow-sm transition-all duration-200 font-medium"
          >
            Manage Departments
          </button>
          <button
            onClick={handleCreate}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-5 py-2.5 rounded-xl shadow-md hover:shadow-lg transition-all duration-200 font-medium flex items-center gap-2 transform hover:-translate-y-0.5"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path></svg>
            Add Employee
          </button>
        </div>
      </div>

      <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col md:flex-row flex-wrap gap-4 items-stretch md:items-center">
        <input
          type="text"
          placeholder="Search employees..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm flex-1 min-w-[200px]"
        />
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <option value="All Roles">All Roles</option>
          <option value="Super Admin">Super Admin</option>
          <option value="Admin">Admin</option>
          <option value="HR">HR</option>
          <option value="Employee">Employee</option>
          {customRoles.map(r => <option key={r._id} value={r.name}>{r.name}</option>)}
        </select>
        <select
          value={departmentFilter}
          onChange={(e) => setDepartmentFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          {uniqueDepartments.map(dep => <option key={dep} value={dep}>{dep}</option>)}
        </select>
        <select
          value={locationFilter}
          onChange={(e) => setLocationFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <option value="All Locations">All Locations</option>
          <option value="OFFICE">Office</option>
          <option value="WFH">Remote (WFH)</option>
          <option value="HYBRID">Hybrid</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-xl focus:ring-blue-500 focus:border-blue-500 text-sm"
        >
          <option value="All Status">All Status</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
        </select>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Department</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">Loading...</td>
              </tr>
              ) : employees.filter(emp => {
                const term = searchTerm.toLowerCase();
                const matchSearch = emp.firstName?.toLowerCase().includes(term) ||
                  emp.lastName?.toLowerCase().includes(term) ||
                  emp.email?.toLowerCase().includes(term) ||
                  emp.employeeId?.toLowerCase().includes(term) ||
                  emp.role?.toLowerCase().includes(term) ||
                  emp.department?.name?.toLowerCase().includes(term);
                
                const matchRole = roleFilter === 'All Roles' || emp.role === roleFilter;
                const matchDept = departmentFilter === 'All Departments' || emp.department?.name === departmentFilter;
                const matchLoc = locationFilter === 'All Locations' || (emp.workLocation || 'OFFICE') === locationFilter;
                const matchStatus = statusFilter === 'All Status' || 
                  (statusFilter === 'Active' ? emp.isActive !== false : emp.isActive === false);
                
                return matchSearch && matchRole && matchDept && matchLoc && matchStatus;
              }).length === 0 ? (
              <tr>
                <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">No employees found</td>
              </tr>
            ) : (
              employees.filter(emp => {
                const term = searchTerm.toLowerCase();
                const matchSearch = emp.firstName?.toLowerCase().includes(term) ||
                  emp.lastName?.toLowerCase().includes(term) ||
                  emp.email?.toLowerCase().includes(term) ||
                  emp.employeeId?.toLowerCase().includes(term) ||
                  emp.role?.toLowerCase().includes(term) ||
                  emp.department?.name?.toLowerCase().includes(term);
                
                const matchRole = roleFilter === 'All Roles' || emp.role === roleFilter;
                const matchDept = departmentFilter === 'All Departments' || emp.department?.name === departmentFilter;
                const matchLoc = locationFilter === 'All Locations' || (emp.workLocation || 'OFFICE') === locationFilter;
                const matchStatus = statusFilter === 'All Status' || 
                  (statusFilter === 'Active' ? emp.isActive !== false : emp.isActive === false);
                
                return matchSearch && matchRole && matchDept && matchLoc && matchStatus;
              }).map((emp) => (
                <tr key={emp._id} onClick={() => handleViewProfile(emp)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{emp.employeeId || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {emp.firstName} {emp.lastName}
                    <div className="text-xs text-gray-500 font-normal">{emp.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                      ${emp.role === 'Admin' ? 'bg-purple-100 text-purple-800' : 
                        emp.role === 'HR' ? 'bg-blue-100 text-blue-800' : 
                        emp.role === 'Intern' ? 'bg-orange-100 text-orange-800' : 
                        'bg-gray-100 text-gray-800'}`}>
                      {emp.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{emp.department?.name || 'N/A'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`px-2.5 py-1 inline-flex text-xs leading-4 font-semibold rounded-full ${
                      emp.workLocation === 'HYBRID' 
                        ? 'bg-purple-100 text-purple-800 border border-purple-200' 
                        : emp.workLocation === 'WFH' 
                          ? 'bg-amber-100 text-amber-800 border border-amber-200' 
                          : 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                    }`}>
                      {emp.workLocation === 'HYBRID' ? 'Hybrid' : emp.workLocation === 'WFH' ? 'Remote' : 'Office'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${emp.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                      {emp.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                      {emp.workLocation === 'HYBRID' && (
                        <button 
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedHybridEmployee(emp);
                            setIsHybridModalOpen(true);
                          }} 
                          className="px-3 py-1.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors inline-flex items-center gap-1.5 border border-purple-200 shadow-sm"
                          title="Pre-define WFH / Office days"
                        >
                          <Calendar className="w-3.5 h-3.5 text-purple-600" />
                          Hybrid Schedule
                        </button>
                      )}
                      {emp.isActive ? (
                        <button 
                          type="button"
                          onClick={(e) => handleToggleActive(emp, e)} 
                          className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors inline-flex items-center gap-1.5 border border-red-100"
                          title="Deactivate"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"></path>
                          </svg>
                          Deactivate
                        </button>
                      ) : (
                        <button 
                          type="button"
                          onClick={(e) => handleToggleActive(emp, e)} 
                          className="px-3 py-1.5 text-xs font-medium text-emerald-700 bg-emerald-50 hover:bg-emerald-100 rounded-lg transition-colors inline-flex items-center gap-1.5 border border-emerald-200"
                          title="Activate / Restore"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                          </svg>
                          Activate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {isFormOpen && (
        <EmployeeForm
          employee={editingEmployee}
          initialTab={initialTab}
          onClose={handleFormClose}
        />
      )}
      
      {isProfileOpen && (
        <EmployeeProfileModal
          employee={editingEmployee}
          onClose={() => setIsProfileOpen(false)}
          onEdit={(emp) => handleEdit(emp)}
        />
      )}

      {isHybridModalOpen && selectedHybridEmployee && (
        <HybridScheduleModal
          isOpen={isHybridModalOpen}
          employee={selectedHybridEmployee}
          onClose={() => {
            setIsHybridModalOpen(false);
            setSelectedHybridEmployee(null);
          }}
          onSaved={() => {
            fetchEmployees();
          }}
        />
      )}

      {isDeptModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 py-10">
          <div className="relative mx-auto border w-full max-w-2xl shadow-lg rounded-xl bg-gray-50">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 h-16 relative rounded-t-xl flex items-center px-6">
              <h3 className="text-lg font-bold text-white">Manage Departments</h3>
              <button 
                onClick={() => { setIsDeptModalOpen(false); fetchEmployees(); }} 
                className="absolute top-4 right-4 text-white hover:text-gray-200 bg-black bg-opacity-20 rounded-full p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            <div className="p-6">
              <Departments />
            </div>
          </div>
        </div>
      )}

      {isRolesModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 py-10">
          <div className="relative mx-auto border w-full max-w-2xl shadow-lg rounded-xl bg-gray-50">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-700 h-16 relative rounded-t-xl flex items-center px-6">
              <h3 className="text-lg font-bold text-white">Manage Roles</h3>
              <button 
                onClick={() => { setIsRolesModalOpen(false); fetchCustomRoles(); fetchEmployees(); }} 
                className="absolute top-4 right-4 text-white hover:text-gray-200 bg-black bg-opacity-20 rounded-full p-2 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
              </button>
            </div>
            <div className="p-6">
              <CustomRoles />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;
